import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Plus, Loader2, Building2, Trash2, ChevronRight, Users as UsersIcon, LogIn,
} from 'lucide-react';
import { toast } from 'sonner';

export default function CompaniesPage() {
  const navigate = useNavigate();
  const { realCompanyId, switchCompany, activeCompanyId } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [form, setForm] = useState({ name: '', slug: '', logo_url: '' });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API_URL}/companies/stats`);
      setCompanies(res.data);
    } catch (e) {
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const slugify = (value) =>
    value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleNameChange = (val) => {
    setForm((f) => ({ ...f, name: val, slug: f.slug ? f.slug : slugify(val) }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: slugify(form.slug || form.name),
        ...(form.logo_url ? { logo_url: form.logo_url } : {}),
      };
      await axios.post(`${API_URL}/companies`, payload);
      toast.success('Empresa creada');
      setCreateOpen(false);
      setForm({ name: '', slug: '', logo_url: '' });
      fetchCompanies();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear empresa');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (company) => {
    try {
      await axios.patch(`${API_URL}/companies/${company.id}`, { is_active: !company.is_active });
      toast.success(`Empresa ${company.is_active ? 'desactivada' : 'activada'}`);
      fetchCompanies();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar empresa');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await axios.delete(`${API_URL}/companies/${deleteTarget.id}`);
      toast.success('Empresa eliminada');
      setDeleteTarget(null);
      fetchCompanies();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSwitchAndOpen = async (company) => {
    try {
      await switchCompany(company.id);
      toast.success(`Trabajando como ${company.name}`);
    } catch (e) {
      toast.error('No se pudo cambiar de empresa');
    }
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#7FD02E]" /> Empresas
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestiona todas las empresas del sistema y sus equipos.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-[#7FD02E] hover:bg-[#6BB825] text-white"
          data-testid="create-company-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Crear empresa
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : companies.length === 0 ? (
            <div className="p-10 text-center text-gray-500">
              No hay empresas registradas.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead className="text-center">Usuarios</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((c) => (
                  <TableRow key={c.id} className="hover:bg-gray-50">
                    <TableCell>
                      <button
                        onClick={() => navigate(`/empresas/${c.id}`)}
                        className="flex items-center gap-3 group"
                      >
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#7FD02E]/20 to-[#7FD02E]/5 flex items-center justify-center text-[#7FD02E] font-semibold">
                          {c.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-900 group-hover:text-[#7FD02E]">
                            {c.name}
                          </p>
                          {c.id === realCompanyId && (
                            <span className="text-xs text-gray-400">Empresa principal</span>
                          )}
                        </div>
                      </button>
                    </TableCell>
                    <TableCell className="text-gray-600 font-mono text-sm">{c.slug}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="gap-1">
                        <UsersIcon className="w-3 h-3" /> {c.user_count}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          checked={c.is_active}
                          onCheckedChange={() => handleToggleActive(c)}
                        />
                        <span className="text-xs text-gray-500">
                          {c.is_active ? 'Activa' : 'Inactiva'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {c.id !== activeCompanyId && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSwitchAndOpen(c)}
                            title="Trabajar como esta empresa"
                          >
                            <LogIn className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/empresas/${c.id}`)}
                        >
                          Detalle <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteTarget(c)}
                          disabled={c.id === realCompanyId}
                          title={c.id === realCompanyId ? 'No puedes eliminar tu propia empresa' : 'Eliminar empresa'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Nueva empresa</DialogTitle>
              <DialogDescription>
                Crea una empresa nueva. Luego podrás agregar sus usuarios desde la vista de detalle.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  required
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Acme Corp"
                />
              </div>
              <div>
                <Label htmlFor="slug">Slug (identificador único) *</Label>
                <Input
                  id="slug"
                  required
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                  placeholder="acme-corp"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Se usa para registros públicos. Solo minúsculas, números y guiones.
                </p>
              </div>
              <div>
                <Label htmlFor="logo_url">URL del logo (opcional)</Label>
                <Input
                  id="logo_url"
                  value={form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={actionLoading} className="bg-[#7FD02E] hover:bg-[#6BB825] text-white">
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Crear empresa
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar empresa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminarán <strong>{deleteTarget?.name}</strong> y
              todos sus usuarios, tickets, proyectos y datos relacionados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Eliminar definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
